import { supabase } from '@/integrations/supabase/client';
import { getEdgeFunctionUrl } from '@/utils/supabaseUrl';

export interface BenchmarkBotConnection {
  id: string;
  user_id: string;
  telegram_chat_id: number;
  telegram_username?: string;
  telegram_user_id: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface PhoneVerification {
  id: string;
  telegram_user_id: number;
  telegram_username?: string;
  phone_number: string;
  normalized_phone: string;
  verification_status: 'pending' | 'verified' | 'rejected';
  user_id?: string;
  created_at: string;
  verified_at?: string;
  attempts_count: number;
  last_attempt_at: string;
}

/**
 * Check if the benchmark bot is configured
 */
export const isBenchmarkBotConfigured = async (): Promise<boolean> => {
  try {
    const { data, error } = await supabase.functions.invoke('telegram-benchmark-bot', {
      body: { action: 'test-connection' }
    });
    
    if (error) {
      console.error('Error checking benchmark bot:', error);
      return false;
    }
    
    return data?.success === true;
  } catch (error) {
    console.error('Failed to check benchmark bot:', error);
    return false;
  }
};

/**
 * Setup webhook for the benchmark bot
 */
export const setupBenchmarkBotWebhook = async (): Promise<{ success: boolean; error?: string }> => {
  try {
    const webhookUrl = getEdgeFunctionUrl('telegram-benchmark-bot');
    
    const { data, error } = await supabase.functions.invoke('telegram-benchmark-bot', {
      body: { 
        action: 'setup-webhook',
        url: webhookUrl
      }
    });
    
    if (error) {
      console.error('Error setting up benchmark bot webhook:', error);
      return { success: false, error: error.message };
    }
    
    return { success: data?.success || false, error: data?.error };
  } catch (error) {
    console.error('Failed to setup benchmark bot webhook:', error);
    return { success: false, error: (error as Error).message };
  }
};

/**
 * Get webhook info for the benchmark bot
 */
export const getBenchmarkBotWebhookInfo = async (): Promise<any> => {
  try {
    const { data, error } = await supabase.functions.invoke('telegram-benchmark-bot', {
      body: { 
        action: 'get-webhook-info'
      }
    });
    
    if (error) {
      console.error('Error getting benchmark bot webhook info:', error);
      return null;
    }
    
    return data;
  } catch (error) {
    console.error('Failed to get benchmark bot webhook info:', error);
    return null;
  }
};

/**
 * Verify phone number for Telegram access
 */
export const verifyPhoneNumber = async (
  telegramUserId: number,
  phoneNumber: string,
  telegramUsername?: string
): Promise<{ success: boolean; error?: string; hasAccess?: boolean }> => {
  try {
    const { data, error } = await supabase.functions.invoke('telegram-benchmark-bot', {
      body: { 
        action: 'verify-phone',
        telegramUserId,
        phoneNumber,
        telegramUsername
      }
    });
    
    if (error) {
      return { success: false, error: error.message };
    }
    
    return { 
      success: data?.success || false, 
      error: data?.error,
      hasAccess: data?.hasAccess
    };
  } catch (error) {
    return { success: false, error: (error as Error).message };
  }
};

/**
 * Get benchmark bot connection for a user
 */
export const getBenchmarkBotConnection = async (userId: string): Promise<BenchmarkBotConnection | null> => {
  try {
    const { data, error } = await supabase.functions.invoke('telegram-benchmark-bot', {
      body: { 
        action: 'get-user-connection',
        userId
      }
    });
    
    if (error || !data?.connection) {
      return null;
    }
    
    return data.connection as BenchmarkBotConnection;
  } catch (error) {
    console.error('Failed to get benchmark bot connection:', error);
    return null;
  }
};

/**
 * Get all benchmark bot connections
 */
export const getAllBenchmarkBotConnections = async (): Promise<BenchmarkBotConnection[]> => {
  try {
    const { data, error } = await supabase.functions.invoke('telegram-benchmark-bot', {
      body: { 
        action: 'get-all-connections'
      }
    });
    
    if (error) {
      console.error('Error fetching benchmark bot connections:', error);
      return [];
    }
    
    return (data?.connections || []) as BenchmarkBotConnection[];
  } catch (error) {
    console.error('Failed to get benchmark bot connections:', error);
    return [];
  }
};

/**
 * Get phone verification attempts
 */
export const getPhoneVerifications = async (): Promise<PhoneVerification[]> => {
  try {
    const { data, error } = await supabase.functions.invoke('telegram-benchmark-bot', {
      body: { 
        action: 'get-phone-verifications'
      }
    });
    
    if (error) {
      console.error('Error fetching phone verifications:', error);
      return [];
    }
    
    return (data?.verifications || []) as PhoneVerification[];
  } catch (error) {
    console.error('Failed to get phone verifications:', error);
    return [];
  }
};

/**
 * Disconnect user from benchmark bot
 */
export const disconnectFromBenchmarkBot = async (userId: string): Promise<{ success: boolean; error?: string }> => {
  try {
    const { data, error } = await supabase.functions.invoke('telegram-benchmark-bot', {
      body: { 
        action: 'disconnect-user',
        userId
      }
    });
    
    if (error) {
      return { success: false, error: error.message };
    }
    
    return { success: data?.success || false, error: data?.error };
  } catch (error) {
    return { success: false, error: (error as Error).message };
  }
};

/**
 * Get benchmark bot statistics
 */
export const getBenchmarkBotStats = async () => {
  try {
    const { data, error } = await supabase.functions.invoke('telegram-benchmark-bot', {
      body: { 
        action: 'get-stats'
      }
    });
    
    if (error) {
      console.error('Error getting benchmark bot stats:', error);
      return {
        totalConnections: 0,
        totalEntriesToday: 0,
        totalPhoneVerifications: 0,
        verifiedToday: 0
      };
    }
    
    return {
      totalConnections: data?.totalConnections || 0,
      totalEntriesToday: data?.totalEntriesToday || 0,
      totalPhoneVerifications: data?.totalPhoneVerifications || 0,
      verifiedToday: data?.verifiedToday || 0
    };
  } catch (error) {
    console.error('Failed to get benchmark bot stats:', error);
    return {
      totalConnections: 0,
      totalEntriesToday: 0,
      totalPhoneVerifications: 0,
      verifiedToday: 0
    };
  }
};
